
import React, { useState, useRef, useEffect } from 'react';
import { testimonials } from '../assets/images';
import { Star, ArrowRight, ArrowLeft, Quote } from 'lucide-react';
import { cn } from '@/lib/utils';

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const testimonialsRef = useRef<HTMLDivElement>(null);

  const handleNext = () => {
    setActiveIndex((prev) => (prev === testimonials.length - 1 ? 0 : prev + 1));
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observeElement = () => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('animate-fade-up', 'opacity-100');
              observer.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.1,
          rootMargin: '0px 0px -100px 0px',
        }
      );

      if (testimonialsRef.current) {
        testimonialsRef.current.classList.add('opacity-0');
        observer.observe(testimonialsRef.current);
      }

      return observer;
    };

    const observer = observeElement();
    
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <section className="py-20 bg-pkgreen-600 relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20"></div>
      <div className="absolute inset-0 bg-pkgreen-900/50 backdrop-blur-sm"></div>
      
      <div ref={testimonialsRef} className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <span className="text-pkgreen-200 font-display uppercase tracking-wider text-sm mb-2 block">
            Client Feedback
          </span>
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-white">
            What Our Clients Say
          </h2>
          <p className="max-w-3xl mx-auto text-white/80 text-lg">
            We take pride in exceeding client expectations and creating lasting relationships.
            Here's what some of our clients have to say about working with PK Landscape Associates.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Testimonial Slider */}
          <div className="relative glass-panel bg-white/10 backdrop-blur-md rounded-xl p-6 md:p-10 shadow-xl">
            <div className="absolute top-0 left-0 transform -translate-x-4 -translate-y-4">
              <Quote className="h-16 w-16 text-pkgreen-200/30" />
            </div>
            
            <div className="relative">
              {testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  className={cn(
                    "transition-all duration-700 ease-smooth",
                    activeIndex === index
                      ? "opacity-100 translate-x-0"
                      : "opacity-0 absolute inset-0 translate-x-8"
                  )}
                >
                  <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
                    <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0 border-2 border-white/40">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="flex mb-2">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={cn(
                              "h-5 w-5",
                              i < testimonial.rating
                                ? "text-yellow-400 fill-yellow-400"
                                : "text-gray-300"
                            )}
                          />
                        ))}
                      </div>
                      <p className="text-white/90 text-lg md:text-xl italic mb-6">
                        "{testimonial.text}"
                      </p>
                      <div>
                        <h4 className="text-white font-medium text-lg">
                          {testimonial.name}
                        </h4>
                        <p className="text-white/70">
                          {testimonial.location}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Slider Controls */}
            <div className="flex justify-between mt-8">
              <div className="flex space-x-2">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveIndex(index)}
                    className={cn(
                      "w-3 h-3 rounded-full transition-all duration-300 ease-smooth",
                      activeIndex === index
                        ? "bg-white w-8"
                        : "bg-white/40 hover:bg-white/60"
                    )}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handlePrev}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-300"
                  aria-label="Previous testimonial"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={handleNext}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-all duration-300"
                  aria-label="Next testimonial"
                >
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
